/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as Teams from '@microsoft/teams-js';
import * as React from 'react';

import { useHistory } from 'react-router-dom';

import { ApprovalRequestType } from '../../../data/types.generated';
import { LogCategory, ScenarioName } from '../../../logger';
import { CreateEsignApprovalMobileComponent } from '../../../mobile/Esign/CreateEsignApproval/CreateEsignApprovalMobileComponent';
import { acceptedFileTypes, getDocusignFileService } from '../../../service/file-upload/DocusignFileService';
import { ApprovalCreator, ApprovalType } from '../../../service/gen';
import {
    ApprovalResponseType,
    DraftEnvelopeInput,
    ParticipantDetails,
    ParticipantRole,
    SignApprovalType,
} from '../../../service/gen/esign';
import { newGuid } from '../../../utilities/AppUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { CreateEsignApprovalComponent } from '../CreateEsignApproval/CreateEsignApproval.component';
import { CreateEsignApprovalComponentProps } from '../CreateEsignApproval/CreateEsignApprovalComponentProps';
import { mobileOnCreateSuccess } from '../CreateEsignApproval/mobileOnCreateSuccess';
import { EsignIframeComponent } from '../EsignIframe/EsignIframe.component';
import { storeThatUserHasUsedEsign } from '../hooks/useCoachMarkState/useCoachMarkState';
import { Recipient } from '../RecipientList';
import { useCreateDocusignEnvelopeForm } from '../utils/forms';
import { createDocusignApproval } from './createDocusignApproval';

export const envelopeInputKey = 'ComposeExtensionEnvelopeInput';

export interface CreateDocusignApprovalContainerProps {
    title: string;
    // Sets title if user switches between approval type
    setTitle: (value: string) => void;
    details: string;
    // Sets details if user switches between approval type
    setDetails: (value: string) => void;
    // Should show back button nav bar (applies for mobile personal app)
    showNavigationBar?: boolean;
    // Callback on dismissing the modal of this component when specified
    onDismiss?: () => void;
    // Set loading page in parent component
    setLoadingPage: (value: boolean) => void;
    // Set loading page message in parent component
    setLoadingPageMessage: (message: string) => void;
    // Set error page in parent component
    setErrorPage: (value: boolean) => void;
    // Set message on error page
    setErrorPageMessage?: (value: string) => void;
    // Set the page for DocuSign login
    setLoginPage: (value: boolean) => void;
    // Sets the visibility of the provider dropdowns.
    setShowEsignHeader: (value: boolean) => void;
    // Callback on back button clicked
    onBack?: () => void;
    // The custom footer component which will be rendered in the modal footer
    customFooterComponent?: JSX.Element;
    // Callback on iframe displayed
    onEsignIframeDisplay?: () => void;
}

export const EnvelopeParticipantsMapper = (recipients: Recipient[], completeInOrder: boolean): ParticipantDetails[] => {
    const participantDetails = new Array<ParticipantDetails>();
    recipients.forEach((recipient: Recipient): void => {
        const participant: ParticipantDetails = {
            Email: recipient.persona.email,
            // If we need to complete in order then apply the order
            Order: completeInOrder ? Number.parseInt(recipient.order, 10) : 1,
            Role:
                recipient.role === 0
                    ? ParticipantRole.SIGNER
                    : recipient.role === 1
                    ? ParticipantRole.CC
                    : ParticipantRole.VIEWER,
            // If text is different than email then it contains the persons full name
            FullName: recipient.name,
        };
        participantDetails.push(participant);
    });
    return participantDetails;
};

export const CreateDocusignApprovalContainer: React.FC<CreateDocusignApprovalContainerProps> = (
    props: CreateDocusignApprovalContainerProps
): JSX.Element => {
    const {
        title,
        setTitle,
        details,
        setDetails,
        onDismiss,
        showNavigationBar,
        setLoadingPage,
        setLoadingPageMessage,
        setErrorPage,
        setErrorPageMessage,
        setShowEsignHeader,
        onBack,
        customFooterComponent,
        onEsignIframeDisplay,
    } = props;

    const form = useCreateDocusignEnvelopeForm({ title, setTitle, details, setDetails });
    const [envelopeUrl, setEnvelopeUrl] = React.useState<string>(null);
    const history = useHistory();

    const host = getAppHost();
    const logger = host.getLogger();
    const fileUploadAcceptedTypes = acceptedFileTypes.join(',');

    const fileService = getDocusignFileService();
    const logCategory = LogCategory.CreateEsignApprovalView;

    const stageViewEnabled = host.getEcsConfig().MicrosoftTeamApprovals.DocusignStageViewEnabled;

    React.useEffect((): void => {
        if (form.filesInput.value && form.filesInput.value.length > 0 && form.filesInput.value[0]?.templateId != null) {
            // Template has been selected, redirect user directly to DocuSign.
            onNext(true);
        }
    }, [form.filesInput.value]);

    /**
     * Called when send or prepare button is pressed
     */
    const onNext = (isDraft?: boolean): void => {
        storeThatUserHasUsedEsign();

        const clientRequestGUID = newGuid();
        if (setLoadingPage) {
            setLoadingPage(true);
        }

        const approvalType = ApprovalType.ESign;
        const file = form.filesInput.value[0];
        // Don't map if created from template.
        const participantDetails =
            file.templateId == null
                ? EnvelopeParticipantsMapper(form.recipientsInput.value, form.completeInOrderInput.value)
                : [];

        logger.logTraceInfo(logCategory, `Clicked on create approval submit, approval type ${approvalType}`);

        const userRoles = form.recipientsInput.value.map((recipient: Recipient): string => {
            return recipient.role === ParticipantRole.SIGNER ? 'SIGNER' : ParticipantRole.CC ? 'CC' : 'VIEWER';
        });
        const title = form.titleInput.value.trim();
        const details = form.detailsInput.value.trim();

        logger.logCreateEsignApproval({
            titleSize: form.titleInput.value.length,
            numberOfApprovers: form.recipientsInput.value.length,
            detailsSize: details?.length ?? 0,
            clientRequestId: clientRequestGUID,
            correlationId: clientRequestGUID,
            hasItem: file.documentBase64 !== null,
            itemType:
                form.filesInput.value && form.filesInput.value.length > 0 && file.documentBase64
                    ? 'desktopDocument'
                    : 'DocusignDocument',
            itemSize: '0',
            signApprovalType: 'Docusign',
            userRoles: userRoles.toString(),
            completeInOrder: form.completeInOrderInput.value,
            isDraft,
        });

        const envelopeInput: DraftEnvelopeInput = {
            DocumentName: file.documentName,
            DocumentBase64: file.documentBase64,
            TemplateId: file.templateId,
            EsignToken: '',
            ApiEndpoint: '',
            Title: title !== '' ? title : undefined,
            Details: details !== '' ? details : undefined,
            SignApprovalType: SignApprovalType.Docusign,
            AssignedTo: [],
            Type: ApprovalResponseType.Basic,
            ClientRequestId: clientRequestGUID,
            ApprovalType: ApprovalType.ESign,
            FirstTimeProvisioning: false,
            Priority: null,
            ParticipantDetails: participantDetails,
            Creator: ApprovalCreator.PersonalApp,
            UserName: host.getUserPrincipalName(),
            IsDraft: isDraft,
            Item: {
                Link: file.documentId,
            },
        };

        const isPersonalApp = onDismiss || showNavigationBar;

        if (isPersonalApp) {
            createDocusignApproval(
                envelopeInput,
                setEnvelopeUrl,
                setLoadingPage,
                setLoadingPageMessage,
                setErrorPage,
                setErrorPageMessage
            ).then((): void => {
                setShowEsignHeader(false);
                if (showNavigationBar) {
                    mobileOnCreateSuccess(history);
                } else if (envelopeInput.IsDraft && stageViewEnabled) {
                    onEsignIframeDisplay();
                } else if (!envelopeInput.IsDraft) {
                    onDismiss();
                }
            });
        } else {
            // Save envelope input data in localStorage for later retrieval, as this current context will be lost.
            // The call to dialog submit will trigger a TaskModuleContinueResponse which navigates directly to this component, without its parent.
            localStorage.setItem(envelopeInputKey, JSON.stringify(envelopeInput));
            logger.logTraceInfo(logCategory, 'Triggering background Approvals creation using bot');
            logger.logTraceDebug(logCategory, 'CreateEnvelope:onNext end for chat/channel');
            logger.flushLogs((): void => {
                // reduce input payload size by setting document base64 to empty.
                envelopeInput.DocumentBase64 = '';
                Teams.dialog.url.submit(envelopeInput);
                setShowEsignHeader(false);
            });
        }
    };

    const componentProps: CreateEsignApprovalComponentProps = {
        form,
        fileService,
        onNext,
        fileUploadAcceptedTypes,
        onBack,
        customFooterComponent,
        signApprovalType: SignApprovalType.Docusign,
        stageViewEnabled,
    };

    const createComponent = host.isMobile() ? (
        <CreateEsignApprovalMobileComponent {...componentProps} />
    ) : (
        <CreateEsignApprovalComponent {...componentProps} />
    );

    return envelopeUrl == null ? (
        createComponent
    ) : stageViewEnabled ? (
        <EsignIframeComponent
            src={envelopeUrl}
            showHeader={false}
            approvalType={ApprovalRequestType.Docusign}
            onMessageProcessed={onDismiss}
            scenarioName={ScenarioName.CreateEsignApproval}
        />
    ) : null;
};
